import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { BottomNav } from "~/components/BottomNav";
import { CallPointShortagePromptHost } from "~/components/CallPointShortagePrompt";
import { ErrorModal } from "~/components/ErrorModal";
import { PointShortagePrompt } from "~/components/PointShortagePrompt";
import {
  MESSAGE_SEND_POINT,
  MIN_POINT_FOR_VIDEO_CALL,
  MIN_POINT_FOR_VOICE_CALL,
} from "~/constants/actionsPoint";
import { CALL_TYPE } from "~/constants/call";
import { TEXT } from "~/constants/text";
import { CallEndedModal } from "~/features/call/components/CallEndedModal";
import { CallOverlay } from "~/features/call/components/CallOverlay";
import { useCallCleanup } from "~/features/call/hooks/useCallCleanup";
import { mapCallEndReasonToEvent } from "~/features/call/utils/callEndEvent";
import { DemoCallOverlay } from "~/features/demoCall/components/DemoCallOverlay";
import { NewsModal } from "~/features/news/components/NewsModal";
import { InsufficientPointNotice } from "~/features/purchase/components/InsufficientPointNotice";
import { InsufficientPointSheet } from "~/features/purchase/components/InsufficientPointSheet";
import { PurchaseQuickChargeSheet } from "~/features/purchase/components/PurchaseQuickChargeSheet";
import { useNotificationPolling } from "~/features/settings/notifications/hooks/useNotificationPolling";
import { SignupBonusModal } from "~/features/signupBonus/SignupBonusModal";
import { useDelayedUnmount } from "~/hooks/useDelayedUnmount";
import { useHasPurchased } from "~/hooks/useHasPurchased";
import { usePrefetchGiftImages } from "~/hooks/usePrefetchGiftImages";
import { useResyncFirebaseUserId } from "~/hooks/useResyncFirebaseUserId";
import { useSendCallRequest } from "~/hooks/useSendCallRequest";
import { isOnCallNow } from "~/lib/callActivity";
import { getAvatarUrl } from "~/lib/imageUrl";
import {
  FIREBASE_EVENT_NAME,
  trackFirebaseEvent,
} from "~/lib/tracking/firebase";
import { useAuthStore } from "~/stores/useAuthStore";
import { useCallPointShortagePromptStore } from "~/stores/useCallPointShortagePromptStore";
import { useCallStore } from "~/stores/useCallStore";
import { useInsufficientPointNoticeStore } from "~/stores/useInsufficientPointNoticeStore";
import { useInsufficientPointSheetStore } from "~/stores/useInsufficientPointSheetStore";
import { useNewsModalStore } from "~/stores/useNewsModalStore";
import { usePointShortagePromptStore } from "~/stores/usePointShortagePromptStore";
import { useQuickChargeStore } from "~/stores/useQuickChargeStore";
import { useSignupBonusStore } from "~/stores/useSignupBonusStore";
import { isCallPointShortagePromptTargetWindow } from "~/utils/callPointShortagePrompt";
import { markPointShortageCall } from "~/utils/lastPointShortageCall";
import { shouldShowLaunchCampaignIntro } from "~/utils/launchCampaignIntro";
import { markPurchaseSurfaceSeen } from "~/utils/purchaseSurfaceSeen";

const AuthenticatedLayout = () => {
  useNotificationPolling();
  useCallCleanup();
  // 通話/ライブ後レビューのギフト画像を起動後アイドル時に先読みし、初回表示のもたつきを防ぐ
  usePrefetchGiftImages();
  // セッション復元で LOGIN_SUCCESS を経由しなかったユーザーの Firebase user_id を補完通知する
  useResyncFirebaseUserId();
  const { location } = useRouterState();
  const navigate = useNavigate();
  const phase = useCallStore((s) => s.phase);
  const partnerName = useCallStore((s) => s.partnerName);
  const callType = useCallStore((s) => s.callType);
  // ストアにはアバター ID が入るため、表示用に URL へ変換する
  const partnerAvatarId = useCallStore((s) => s.partnerAvatarUrl);
  const partnerAvatarUrl = getAvatarUrl(partnerAvatarId);
  const partnerId = useCallStore((s) => s.partnerId);
  const { sendCallRequest, isPending: isSendingCallRequest } =
    useSendCallRequest();
  const newsModalOpen = useNewsModalStore((s) => s.isOpen);
  const signupBonusOpen = useSignupBonusStore((s) => s.isOpen);
  const quickChargeOpen = useQuickChargeStore((s) => s.open);
  const quickChargeSource = useQuickChargeStore((s) => s.source);
  const quickChargeScene = useQuickChargeStore((s) => s.scene);
  const quickChargeOnSuccess = useQuickChargeStore((s) => s.onChargeSuccessCb);
  const hideQuickCharge = useQuickChargeStore((s) => s.hide);
  const insufficientSheetOpen = useInsufficientPointSheetStore((s) => s.open);
  const insufficientSheetNeeded = useInsufficientPointSheetStore(
    (s) => s.needed,
  );
  const insufficientSheetOnClose = useInsufficientPointSheetStore(
    (s) => s.onCloseCb,
  );
  const insufficientSheetOnSuccess = useInsufficientPointSheetStore(
    (s) => s.onChargeSuccessCb,
  );
  const insufficientSheetShakeSignal = useInsufficientPointSheetStore(
    (s) => s.shakeSignal,
  );
  const hideInsufficientSheet = useInsufficientPointSheetStore((s) => s.hide);
  const insufficientNoticeVisible = useInsufficientPointNoticeStore(
    (s) => s.visible,
  );
  const insufficientNoticeOnTap = useInsufficientPointNoticeStore(
    (s) => s.onTapCb,
  );
  const hideInsufficientNotice = useInsufficientPointNoticeStore((s) => s.hide);
  const ptPromptOpen = usePointShortagePromptStore((s) => s.open);
  const ptPromptDisplayed = usePointShortagePromptStore((s) => s.displayed);
  // 通話 pt不足の文脈モーダルの対象判定。
  // 課金経験は BE 問い合わせなので、通話が切れた瞬間に間に合うよう、
  // 窓の内側にいるユーザーでは先に引いておく（対象外のユーザーでは発火しない）。
  const callPromptWindow = isCallPointShortagePromptTargetWindow(
    useAuthStore((s) => s.userId) ?? "",
  );
  const hasPurchased = useHasPurchased(callPromptWindow);
  const isCallPromptTarget = callPromptWindow && hasPurchased === false;
  const callPromptContext = useCallPointShortagePromptStore((s) => s.context);
  // 閉じても slide-down 退場アニメのぶんだけマウントを維持する。
  const quickChargeRender = useDelayedUnmount(quickChargeOpen);
  const insufficientSheetRender = useDelayedUnmount(insufficientSheetOpen);
  const insufficientNoticeRender = useDelayedUnmount(insufficientNoticeVisible);

  useEffect(() => {
    const handler = (e: Event) => {
      const { userid, noti_type } = (
        e as CustomEvent<{ userid?: string; noti_type?: string | number }>
      ).detail;
      // noti_type 整備完了までは "noti_type_<値>" 形式で送る（値未提供時は unknown）。
      // パラメータ名は BQ 側クエリ互換のため campaign_id のまま変えない
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PUSH_OPEN, {
        campaign_id: `noti_type_${noti_type ?? "unknown"}`,
      });
      if (!userid) return;
      // 通話中・ライブ視聴中はメッセージ画面へ割り込ませない
      if (isOnCallNow()) return;
      navigate({
        to: "/messages/$conversationId",
        params: { conversationId: userid },
      });
    };
    window.addEventListener("pushTapped", handler);
    return () => window.removeEventListener("pushTapped", handler);
  }, [navigate]);

  useEffect(() => {
    // location.pathname はミュータブル値のため deps に含めず、effect 本体内で参照する
    const pathname = location.pathname;
    if (
      phase.type === "ended" &&
      !pathname.startsWith("/call") &&
      phase.reason === "pointShortage"
    ) {
      // ここから先は必ず購入導線（文脈モーダル or 汎用シート）を出すので、
      // チャットの先回りモーダルの抑止用に記録する。イベントは変えない。
      markPurchaseSurfaceSeen();
      // 文脈モーダルは従来の pt不足シートを「置き換える」ものなので、露出そのものは
      // 変えない。この2イベントは出し分けに関わらず従来どおり同じ条件で立てる
      // （露出を固定したまま「見せ方」の効果だけを分離して測るため）。
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PT_ZERO_REACHED, {
        last_action: callType === "voice" ? "voicecall" : "videocall",
      });
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PAYWALL_VIEW, {
        trigger: "pt_zero",
      });

      // ビデオ通話が pt 不足で自動終了した、登録7日以内の未課金ユーザーには
      // 汎用ポイントストアではなく相手の名前と顔で出す（Phase 1）。
      // 課金経験が判定できていないあいだは従来どおりのシートに倒す（安全側）。
      const callState = useCallStore.getState();
      if (
        callType === CALL_TYPE.VIDEO &&
        isCallPromptTarget &&
        callState.partnerId
      ) {
        // 再訪トリガー（チャット / プロフィール / 通話ボタン再タップ）が
        // この通話を参照できるよう、表示の可否に関わらず先に記録する。
        markPointShortageCall(callState.partnerId);
        useCallPointShortagePromptStore.getState().show({
          trigger: "call_end",
          partnerId: callState.partnerId,
          partnerName: callState.partnerName ?? "",
          partnerPhoto: getAvatarUrl(callState.partnerAvatarUrl),
          // Phase 1 は pointShortage（= detail 201）でしか通らないが、
          // 誤爆したときに気づけるよう実際の値を計測に載せる。
          callEndDetail: mapCallEndReasonToEvent(phase.reason)
            .call_ended_event_detail,
          callType: CALL_TYPE.VIDEO,
          balancePt: callState.currentPoint ?? undefined,
          callDurationSec: callState.callDurationSec,
        });
        return;
      }

      useInsufficientPointSheetStore.getState().show({
        needed: {
          label: TEXT.insufficientPointSheet.neededLabelCall,
          value:
            callType === "voice"
              ? MIN_POINT_FOR_VOICE_CALL
              : MIN_POINT_FOR_VIDEO_CALL,
          unit: TEXT.insufficientPointSheet.unitPerMin,
          scene: callType === "voice" ? "voice" : "video",
        },
        onClose: () => useCallStore.getState().reset(),
      });
    }
  }, [phase, location, callType, isCallPromptTarget]);

  const BOTTOM_NAV_VISIBLE_PATHS = [
    "/discover",
    "/live",
    "/board",
    "/messages",
    "/my-page",
  ];

  const showBottomNav = BOTTOM_NAV_VISIBLE_PATHS.some(
    (p) => location.pathname === p || location.pathname === `${p}/`,
  );

  const isCallEnded =
    phase.type === "ended" && !location.pathname.startsWith("/call");

  const handleCloseEndedModal = () => {
    useCallStore.getState().reset();
  };

  const handleFavorite = () => {
    // TODO: お気に入り API 実装後に接続
  };

  const handleCallRequest = async () => {
    if (isSendingCallRequest) return;
    const id = partnerId ?? "";
    if (!id) return;
    const ok = await sendCallRequest({
      partnerId: id,
      partnerName: partnerName ?? "",
    });
    if (ok) useCallStore.getState().reset();
  };

  const handleVoiceCallRequest = async () => {
    if (isSendingCallRequest) return;
    const id = partnerId ?? "";
    if (!id) return;
    const ok = await sendCallRequest({
      partnerId: id,
      partnerName: partnerName ?? "",
      callType: CALL_TYPE.VOICE,
    });
    if (ok) useCallStore.getState().reset();
  };

  // pt不足の先回りモーダルの CTA。計測はストアが持ち、ここはチャージシートを開くだけ。
  const handlePointShortageCharge = () => {
    usePointShortagePromptStore.getState().confirmCharge();
    useInsufficientPointSheetStore.getState().show({
      needed: {
        label: TEXT.insufficientPointSheet.neededLabel,
        value: MESSAGE_SEND_POINT,
        unit: TEXT.purchase.unitPt,
        scene: "chat",
      },
    });
  };

  const handleSendMessage = () => {
    const id = partnerId ?? "";
    useCallStore.getState().reset();
    navigate({
      to: "/messages/$conversationId",
      params: { conversationId: id },
    });
  };

  return (
    <div>
      <Outlet />
      {showBottomNav && <BottomNav />}
      <CallOverlay />
      <DemoCallOverlay />
      <ErrorModal />
      {/* pt不足の先回りモーダル。チャット滞在／相手プロフィール滞在／いいねの
          3トリガーから開くため、feature ではなくここに1つだけ置く。 */}
      {ptPromptDisplayed && (
        <PointShortagePrompt
          open={ptPromptOpen}
          partnerName={ptPromptDisplayed.partnerName}
          partnerPhoto={ptPromptDisplayed.partnerPhoto}
          partnerLastMessage={ptPromptDisplayed.partnerLastMessage}
          isOnline={ptPromptDisplayed.isOnline}
          onCharge={handlePointShortageCharge}
          onDismiss={() => usePointShortagePromptStore.getState().dismiss()}
        />
      )}
      {/* 通話 pt不足の文脈モーダル（Carina・登録7日以内・未課金のビデオ通話のみ）。
          購入結果のネイティブイベントを受け取る必要があるため、閉じたあとも
          ストアに文脈が残っているあいだはマウントを維持する。 */}
      {callPromptContext && <CallPointShortagePromptHost />}
      {isCallEnded &&
        phase.type === "ended" &&
        phase.reason !== "pointShortage" && (
          <CallEndedModal
            reason={phase.reason}
            partnerName={partnerName}
            partnerAvatarUrl={partnerAvatarUrl}
            onClose={handleCloseEndedModal}
            onFavorite={handleFavorite}
            onVideoCall={handleCallRequest}
            onVoiceCall={handleVoiceCallRequest}
            onSendMessage={handleSendMessage}
          />
        )}
      {/* ニュースモーダル（settings / discover から store 経由で開く）。
          開いている間だけマウントする。 */}
      {newsModalOpen && <NewsModal />}
      {/* 新規登録ボーナス（250pt）付与アニメーション。開いている間だけマウントする。 */}
      {signupBonusOpen && <SignupBonusModal />}
      {/* ポイントクイックチャージシート（call / live から store 経由で開く）。
          常時マウントだと useUserPoint が走り続けるため、開いている間だけマウントする。
          open={false} でも退場アニメ中は描画し続ける（useDelayedUnmount）。 */}
      {quickChargeRender && (
        <PurchaseQuickChargeSheet
          open={quickChargeOpen}
          onClose={hideQuickCharge}
          source={quickChargeSource}
          scene={quickChargeScene}
          onChargeSuccess={(points) => quickChargeOnSuccess?.(points)}
        />
      )}
      {/* ポイント不足シート（IAP その場チャージ）。ライブ入室・メッセージ送信など
          複数 feature から useInsufficientPointSheetStore 経由で開く。
          開いている間だけマウントして useUserPoint / useStartPurchase が常時起動しないようにする。
          open={false} でも退場アニメ中は描画し続ける（useDelayedUnmount）。 */}
      {insufficientSheetRender && insufficientSheetNeeded && (
        <InsufficientPointSheet
          open={insufficientSheetOpen}
          needed={insufficientSheetNeeded}
          shakeSignal={insufficientSheetShakeSignal}
          onClose={() => {
            insufficientSheetOnClose?.();
            hideInsufficientSheet();
          }}
          onChargeSuccess={(points) => {
            insufficientSheetOnSuccess?.(points);
            hideInsufficientSheet();
          }}
        />
      )}
      {/* ポイント不足の控えめな通知バナー（通話中・ライブ視聴中に自動表示）。
          タップで上のポイント不足シートを開くか、右スワイプで見送るまで自動的には消えない。
          開いている間だけマウントする。 */}
      {insufficientNoticeRender && (
        <InsufficientPointNotice
          visible={insufficientNoticeVisible}
          onTap={() => insufficientNoticeOnTap?.()}
          onSwipeDismiss={hideInsufficientNotice}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ location }) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw redirect({ to: "/login" });
    }
    // アプリ起動時キャンペーン動画: このセッションでまだ未表示なら、
    // discover 等への遷移前に /launch-campaign を挟む。
    // 自分自身への遷移まで横取りすると無限リダイレクトになるため除外する。
    //
    // ★横取りする前の行き先を next で渡す。動画のあとそこへ戻すため
    //   （渡さないと動画が常に discover へ流し、登録直後のオンボーディングが
    //   一度も表示されないまま終わる。オンボーディングは登録直後の一度きりで、
    //   逃すと二度と取れない）。
    if (
      location.pathname !== "/launch-campaign" &&
      shouldShowLaunchCampaignIntro()
    ) {
      throw redirect({
        to: "/launch-campaign",
        search: { next: location.pathname },
      });
    }
  },
  component: AuthenticatedLayout,
});
